/** Pure helpers for the Trim Video timeline and keyboard shortcuts. */

export const DEFAULT_FPS = 30;

export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** Seconds rounded to one decimal, the precision the time boxes show. */
export const round1 = (t: number): number => Math.round(t * 10) / 10;

/** Moves a player time by whole frames (negative = back), staying inside 0..duration. */
export function nudgeFrame(time: number, frames: number, duration: number, fps = DEFAULT_FPS): number {
  const f = Number.isFinite(fps) && fps > 0 ? fps : DEFAULT_FPS;
  return clamp(time + frames / f, 0, Number.isFinite(duration) && duration > 0 ? duration : Infinity);
}

/** Moves a time by whole seconds, staying inside 0..duration. */
export function nudgeSeconds(time: number, seconds: number, duration: number): number {
  return nudgeFrame(time, seconds * DEFAULT_FPS, duration, DEFAULT_FPS);
}

/** Position on the bar (0..100) for a time. */
export function timeToPercent(t: number, duration: number): number {
  return duration > 0 ? clamp((t / duration) * 100, 0, 100) : 0;
}

/** Time for a pointer x inside a bar (left, width in px). */
export function pointerToTime(clientX: number, left: number, width: number, duration: number): number {
  if (!(width > 0)) return 0;
  return clamp(((clientX - left) / width) * duration, 0, duration);
}

/** Minimum gap kept between a segment's start and end handle. */
export const MIN_GAP = 0.1;

/** Drags a handle without letting start pass end (or end pass start). Result is rounded to 0.1 s. */
export function moveHandle(which: "start" | "end", time: number, start: number, end: number, duration: number): { start: number; end: number } {
  const t = clamp(time, 0, duration);
  if (which === "start") return { start: round1(Math.min(t, end - MIN_GAP)), end };
  return { start, end: round1(Math.max(t, start + MIN_GAP)) };
}

/** Moves item at index by delta (-1 up, +1 down); returns a new array (same array if out of range). */
export function moveItem<T>(list: T[], index: number, delta: number): T[] {
  const to = index + delta;
  if (index < 0 || index >= list.length || to < 0 || to >= list.length) return list;
  const next = list.slice();
  const [item] = next.splice(index, 1);
  next.splice(to, 0, item);
  return next;
}

/** "Output: MP4" line; WebM/MOV/etc. inputs always become .mp4 in exact mode. */
export function outputLabel(mode: "exact" | "fast", inputExt: string): string {
  const ext = inputExt.toLowerCase();
  if (mode === "exact" || ext === "mp4") return "Output: MP4";
  return `Output: .${ext} (same as the original) unless a fast cut falls back to MP4`;
}

/** Text for the actual start after a fast cut. */
export function actualStartText(requestedStart: number, earlyBy: number | undefined, formatTime: (t: number) => string): string {
  const actual = Math.max(0, requestedStart - (earlyBy ?? 0));
  return `Actual start ${formatTime(actual)} (nearest keyframe)`;
}
